import { useState } from 'react';
import { Seo } from '../components/Seo';
import { Footer } from '../components/Footer';
import { BlogHeader } from '../components/BlogHeader';
import { SITE_URL, getStrings, localePath, altLinks, type Lang } from '../lib/i18n';
import { EVENT, MATCH_CARD, type Athlete } from '../lib/eastVsWest';
import { RankingsSection } from '../components/RankingsSection';
import { getEvents, eventPath, formatDate, headlineMatch, athleteFor, athletePath } from '../lib/events';
import { Link } from 'react-router-dom';
import { STORE_IOS, STORE_ANDROID } from '../lib/constants';
import { StoreBadges } from '../components/StoreBadges';

type StoreStrings = ReturnType<typeof getStrings>['store'];

/* ── Small building blocks ─────────────────────────────────────────── */


function StoreButtons({ s, lang, size = 'lg' }: { s: StoreStrings; lang: Lang; size?: 'lg' | 'md' }) {
  return (
    <div className="flex flex-col sm:flex-row items-center gap-4">
      <StoreBadges iosHref={STORE_IOS} androidHref={STORE_ANDROID} s={s} lang={lang} height={size === 'lg' ? 52 : 46} />
    </div>
  );
}

function CtaBanner({ heading, body, s, lang }: { heading: string; body: string; s: StoreStrings; lang: Lang }) {
  return (
    <div className="my-14 rounded-3xl border border-line bg-gradient-to-br from-surface to-elevated p-8 md:p-10 text-center">
      <h2 className="text-2xl md:text-3xl font-extrabold text-ink mb-3">{heading}</h2>
      <p className="text-muted max-w-2xl mx-auto mb-7">{body}</p>
      <div className="flex justify-center"><StoreButtons s={s} lang={lang} size="md" /></div>
    </div>
  );
}

function hueFor(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 360;
  return h;
}

// Responsive: 40px on phones (so both fighters + "VS" fit a narrow card), 52px ≥sm.
function AthleteAvatar({ a }: { a: Athlete }) {
  const h = hueFor(a.name);
  const gid = `avg-${h}`;
  const [broken, setBroken] = useState(false);
  return (
    <div className="relative shrink-0 w-10 h-10 sm:w-[52px] sm:h-[52px] rounded-full overflow-hidden ring-2 ring-white/10 shadow-lg bg-secondary">
      {a.image && !broken ? (
        <img src={a.image} alt={a.name} width={52} height={52} onError={() => setBroken(true)} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <svg viewBox="0 0 52 52" role="img" aria-label={a.name} className="w-full h-full">
          <defs>
            <linearGradient id={gid} x1="0" y1="0" x2="0.4" y2="1">
              <stop offset="0" stopColor={`hsl(${h} 58% 34%)`} />
              <stop offset="1" stopColor={`hsl(${(h + 24) % 360} 62% 15%)`} />
            </linearGradient>
          </defs>
          <rect width="52" height="52" fill={`url(#${gid})`} />
          <circle cx="26" cy="20" r="8.5" fill="rgba(255,255,255,0.92)" />
          <path d="M9 46c0-9.4 7.9-15 17-15s17 5.6 17 15z" fill="rgba(255,255,255,0.92)" />
        </svg>
      )}
      <span className="absolute -bottom-0.5 -right-0.5 text-sm sm:text-base leading-none drop-shadow">{a.flag}</span>
    </div>
  );
}

function AthleteChip({ a, lang, align = 'left' }: { a: Athlete; lang: Lang; align?: 'left' | 'right' }) {
  const prof = athleteFor(a.name);
  // min-w-0 lets this shrink inside the 1fr grid column so the name can truncate
  // instead of pushing the card past the viewport edge on mobile.
  const body = (
    <div className={`flex items-center gap-2 sm:gap-3 min-w-0 ${align === 'right' ? 'flex-row-reverse text-right' : ''}`}>
      <AthleteAvatar a={a} />
      <div className="min-w-0">
        <div className="text-ink font-bold leading-tight truncate">{a.name}</div>
        <div className="text-faint text-xs truncate">{a.nickname ? `“${a.nickname}” · ${a.country}` : a.country}</div>
      </div>
    </div>
  );
  return prof ? (
    <Link to={athletePath(lang, prof.slug)} className="block min-w-0 hover:opacity-90 transition" title={a.name}>{body}</Link>
  ) : body;
}

/* ── Page ──────────────────────────────────────────────────────────── */

export default function EastVsWest({ lang = 'en' as Lang }: { lang?: Lang }) {
  const t = getStrings(lang).eastVsWest;
  const s = getStrings(lang).store;
  const url = `${SITE_URL}${localePath(lang, '/east-vs-west')}`;
  const apBig = ['10 AP', '+15 AP', '1.5×'];

  const eventLd = {
    '@context': 'https://schema.org',
    '@type': 'SportsEvent',
    name: EVENT.name,
    sport: 'Arm wrestling',
    startDate: EVENT.isoDate,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: { '@type': 'Place', name: EVENT.venue, address: `${EVENT.city}, ${EVENT.country}` },
    description: t.metaDescription,
    organizer: { '@type': 'Organization', name: 'East vs West' },
  };
  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: t.faq.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  };

  return (
    <div className="min-h-screen bg-dark">
      <Seo
        title={t.metaTitle}
        description={t.metaDescription}
        canonical={url}
        image={`${SITE_URL}/images/evw28-hero.webp`}
        lang={lang}
        alternates={altLinks('/east-vs-west')}
        jsonLd={[eventLd, faqLd]}
      />

      <BlogHeader lang={lang} />

      {/* ── Event hero ── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/evw28-hero.webp" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-dark/65" />
          <div className="absolute inset-0 bg-gradient-to-b from-dark/30 via-dark/45 to-dark" />
        </div>
        <div className="relative z-10 container mx-auto px-6 py-20 md:py-28 text-center">
          <span className="inline-flex items-center gap-2 bg-primary/15 border border-primary/30 text-primary px-4 py-1.5 rounded-full text-sm font-bold mb-6">
            <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" /> {t.upcomingBadge}
          </span>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-ink mb-4 leading-[1.05]">{EVENT.name}</h1>
          <p className="text-xl md:text-2xl text-gray-700 mb-2 font-semibold">{EVENT.city}, {EVENT.country} · {t.eventDate}</p>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-9">{t.heroTagline}</p>
          <div className="flex justify-center"><StoreButtons s={s} lang={lang} /></div>
        </div>
      </section>

      <main className="container mx-auto px-6 max-w-4xl py-16">
        {/* Intro */}
        <section>
          <span className="text-primary text-sm font-bold uppercase tracking-widest">{t.seriesLabel}</span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-ink mt-2 mb-4">{t.seriesTitle}</h2>
          <p className="text-muted text-lg leading-relaxed mb-4">{t.seriesP1}</p>
          <p className="text-muted text-lg leading-relaxed">{t.seriesP2}</p>
        </section>

        {/* ── Match card ── */}
        <section className="mt-14">
          <span className="text-primary text-sm font-bold uppercase tracking-widest">{t.cardLabel}</span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-ink mt-2 mb-6">{t.cardTitle}</h2>
          {MATCH_CARD.length === 0 ? (
            <div className="rounded-2xl border border-line bg-surface/60 p-6 text-center">
              <p className="text-muted text-lg">{t.cardTba}</p>
            </div>
          ) : (
          <div className="space-y-4">
            {MATCH_CARD.map((m, i) => (
              <div key={i} className={`rounded-2xl border p-4 sm:p-5 md:p-6 ${m.mainEvent ? 'border-primary/50 bg-primary/5' : 'border-line bg-surface/60'}`}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-1 sm:gap-2">
                  <span className={`text-xs font-bold uppercase tracking-wider ${m.mainEvent ? 'text-primary' : 'text-faint'}`}>
                    {m.mainEvent ? `★ ${t.mainEvent} · ` : ''}{t.matchTitle[m.title]}
                  </span>
                  <span className="text-faint text-xs font-semibold sm:text-right">{t.wc[m.wc]} · {t.arm[m.arm]} · {t.format[m.format]}</span>
                </div>
                <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 sm:gap-3">
                  <AthleteChip a={m.a} lang={lang} />
                  <span className="text-primary font-extrabold text-sm md:text-base px-1 sm:px-2">{t.vs}</span>
                  <AthleteChip a={m.b} lang={lang} align="right" />
                </div>
              </div>
            ))}
          </div>
          )}
          {MATCH_CARD.length > 0 ? <p className="text-faint text-sm mt-4">{t.cardProvisional}</p> : null}
        </section>

        <CtaBanner heading={t.cta[0].heading} body={t.cta[0].body} s={s} lang={lang} />

        {/* ── Rankings (official, filterable by weight class) ── */}
        <RankingsSection lang={lang} />

        {/* ── Predictions / AP ── */}
        <section className="mt-14">
          <span className="text-primary text-sm font-bold uppercase tracking-widest">{t.predictLabel}</span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-ink mt-2 mb-4">{t.predictTitle}</h2>
          <p className="text-muted text-lg leading-relaxed mb-6">{t.predictIntro}</p>
          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            {t.apCards.map((small, i) => (
              <div key={i} className="rounded-2xl border border-line bg-surface/60 p-6 text-center">
                <div className="text-3xl font-extrabold text-primary mb-1">{apBig[i]}</div>
                <div className="text-muted text-sm">{small}</div>
              </div>
            ))}
          </div>
          <p className="text-muted text-lg leading-relaxed">{t.predictOutro}</p>
        </section>

        <CtaBanner heading={t.cta[1].heading} body={t.cta[1].body} s={s} lang={lang} />

        {/* ── Event archive (clickable) ── */}
        <section className="mt-4">
          <span className="text-primary text-sm font-bold uppercase tracking-widest">{t.historyLabel}</span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-ink mt-2 mb-2">{t.historyTitle}</h2>
          <p className="text-muted mb-6">{t.archiveHint}</p>
          <div className="space-y-3">
            {/* Upcoming event → the main card above */}
            <a href={localePath(lang, '/east-vs-west')} className="block rounded-2xl border border-primary/40 bg-primary/5 p-5 hover:border-primary transition">
              <div className="flex items-center justify-between gap-2">
                <span className="text-ink font-extrabold">{EVENT.name}</span>
                <span className="text-[11px] font-bold uppercase tracking-wide text-primary bg-primary/10 border border-primary/30 px-2 py-0.5 rounded-full">{t.upcomingTag}</span>
              </div>
              <span className="text-faint text-sm font-semibold">{EVENT.city}, {EVENT.country} · {formatDate(EVENT.isoDate)}</span>
            </a>

            {getEvents().map((e) => {
              const h = headlineMatch(e);
              return (
                <Link key={e.slug} to={eventPath(lang, e.slug)} className="group block rounded-2xl border border-line bg-surface/60 p-5 hover:border-primary/40 transition">
                  <div className="flex items-center justify-between mb-1 gap-2">
                    <span className="text-ink font-extrabold group-hover:text-primary transition">{e.name}</span>
                    <span className="text-faint text-xs font-semibold text-right whitespace-nowrap">{[e.city, e.country].filter(Boolean).join(', ')} · {formatDate(e.isoDate)}</span>
                  </div>
                  {h && (
                    <p className="text-muted text-sm leading-relaxed">
                      <span className="text-ink font-semibold">{h.winner}</span> {h.score.replace('-', '–')} {h.loser}
                      <span className="text-faint"> · {e.matches.length}×</span>
                    </p>
                  )}
                </Link>
              );
            })}
          </div>
        </section>

        {/* ── Track your own ── */}
        <section className="mt-14">
          <span className="text-primary text-sm font-bold uppercase tracking-widest">{t.yourTurnLabel}</span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-ink mt-2 mb-4">{t.yourTurnTitle}</h2>
          <p className="text-muted text-lg leading-relaxed mb-4">{t.yourTurnP1}</p>
          <p className="text-muted text-lg leading-relaxed">{t.yourTurnP2}</p>
        </section>

        {/* ── FAQ ── */}
        <section className="mt-14">
          <h2 className="text-2xl md:text-3xl font-extrabold text-ink mb-6">{t.faqHeading}</h2>
          <div className="space-y-6">
            {t.faq.map((f) => (
              <div key={f.q}>
                <h3 className="text-ink font-bold mb-1">{f.q}</h3>
                <p className="text-muted leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        <CtaBanner heading={t.cta[2].heading} body={t.cta[2].body} s={s} lang={lang} />
      </main>

      <Footer lang={lang} />
    </div>
  );
}
